import { hierarchy, tree, type HierarchyPointNode } from 'd3-hierarchy';

import type { SeatView } from '@/api/seats.api';
import type { SeatTreeNode } from './buildSeatTree';

/**
 * Layout math for the accountability chart. Uses `d3-hierarchy`'s `tree`
 * layout for node POSITIONS only — no imperative d3 DOM mutation, so it
 * runs identically under jsdom (pure arithmetic, no measurement). React
 * renders the positioned cards + SVG connectors.
 *
 * Multiple roots are wrapped under a synthetic super-root so a single
 * `tree()` pass positions the whole forest; the synthetic root is then
 * stripped (real roots sit at depth 0).
 */

export interface PositionedSeat {
  seat: SeatView;
  /** Top-left corner in chart-space px. */
  x: number;
  y: number;
}

export interface SeatEdge {
  parentId: string;
  childId: string;
  /** Parent bottom-centre → child top-centre, chart-space px. */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface SeatLayout {
  nodes: PositionedSeat[];
  edges: SeatEdge[];
  width: number;
  height: number;
}

export interface LayoutOptions {
  nodeWidth: number;
  nodeHeight: number;
  gapX: number;
  gapY: number;
  padding?: number;
}

interface LayoutDatum {
  seat: SeatView | null;
  children: LayoutDatum[];
}

function toDatum(node: SeatTreeNode): LayoutDatum {
  return { seat: node.seat, children: node.children.map(toDatum) };
}

export function layoutSeatTree(roots: SeatTreeNode[], opts: LayoutOptions): SeatLayout {
  const padding = opts.padding ?? 24;
  const stepX = opts.nodeWidth + opts.gapX;
  const stepY = opts.nodeHeight + opts.gapY;

  if (roots.length === 0) {
    return {
      nodes: [],
      edges: [],
      width: opts.nodeWidth + padding * 2,
      height: opts.nodeHeight + padding * 2,
    };
  }

  const syntheticRoot: LayoutDatum = { seat: null, children: roots.map(toDatum) };
  const root = hierarchy<LayoutDatum>(syntheticRoot, (d) => d.children);
  // The returned node is a HierarchyPointNode — x/y are assigned numbers.
  const laidOut = tree<LayoutDatum>().nodeSize([stepX, stepY])(root);

  // Real nodes only (skip the synthetic root at depth 0). Kept for the x /
  // depth math (pure numbers — no seat access, so no narrowing needed).
  const descendants = laidOut.descendants();
  const real = descendants.filter((n) => n.data.seat !== null);
  if (real.length === 0) {
    return {
      nodes: [],
      edges: [],
      width: opts.nodeWidth + padding * 2,
      height: opts.nodeHeight + padding * 2,
    };
  }

  const minX = Math.min(...real.map((n) => n.x));
  const offsetX = padding - minX;

  // d3 `y` = depth * stepY; real roots are at depth 1, so subtract one step
  // to bring them to the top, then pad.
  function toX(n: HierarchyPointNode<LayoutDatum>): number {
    return n.x + offsetX;
  }
  function toY(n: HierarchyPointNode<LayoutDatum>): number {
    return (n.depth - 1) * stepY + padding;
  }

  const nodes: PositionedSeat[] = [];
  const edges: SeatEdge[] = [];
  for (const n of descendants) {
    const seat = n.data.seat;
    if (!seat) continue; // synthetic root — narrows `seat` to SeatView
    nodes.push({ seat, x: toX(n), y: toY(n) });

    const parent = n.parent;
    const parentSeat = parent?.data.seat;
    if (!parent || !parentSeat) continue; // parent is the synthetic root
    edges.push({
      parentId: parentSeat.id,
      childId: seat.id,
      x1: toX(parent) + opts.nodeWidth / 2,
      y1: toY(parent) + opts.nodeHeight,
      x2: toX(n) + opts.nodeWidth / 2,
      y2: toY(n),
    });
  }

  const maxX = Math.max(...nodes.map((p) => p.x));
  const maxDepth = Math.max(...real.map((n) => n.depth - 1));
  const width = maxX + opts.nodeWidth + padding;
  const height = maxDepth * stepY + opts.nodeHeight + padding * 2;

  return { nodes, edges, width, height };
}
